/**
 * Navigation par profil, portée depuis la maquette validée
 * (`docs/maquette/modules/06-…` et `10-…`, objet `SIDEBAR_CONFIG`).
 *
 * Les entrées sont filtrées à l'affichage par les droits renvoyés par l'API :
 * une rubrique hors périmètre n'apparaît pas, et sa route est refusée côté
 * serveur même si le lien est forgé à la main.
 */

import type { NomIcone } from './icons';
import type { Profil, SousProfil } from '../lib/types';

export interface EntreeNavigation {
  ecran: string;
  libelle: string;
  icone: NomIcone;
  /** Pastille numérique, ex. le nombre de devoirs à rendre. */
  pastille?: { valeur: string; ton: 'mint' | 'warning' | 'info' | 'danger' };
  /** Étiquette courte, ex. « IA » ou « AA ». */
  etiquette?: string;
}

export interface SectionNavigation {
  titre: string;
  entrees: EntreeNavigation[];
}

export interface ConfigurationSidebar {
  /** Dégradé de la vignette d'espace, classes Tailwind de la maquette. */
  accent: string;
  initiales: string;
  titre: string;
  sousTitre: string;
  sections: SectionNavigation[];
}

/**
 * Titre ou sous-titre qui désigne l'organisme : le menu y affiche le nom de la
 * marque en vigueur (TF-436), et la vignette ses initiales.
 */
export const LIBELLE_MARQUE = 'TOP FORMATION';

const COMPTE: SectionNavigation = {
  titre: 'Compte',
  entrees: [
    // FAQ interne, ouverte à tous ; la direction y tient les articles (TF-483).
    { ecran: 'help', libelle: 'Aide & FAQ', icone: 'question' },
    { ecran: 'accessibility', libelle: 'Accessibilité', icone: 'accessibilite', etiquette: 'AA' },
    { ecran: 'settings', libelle: 'Paramètres', icone: 'reglages' },
  ],
};

export const SIDEBAR: Record<Profil, ConfigurationSidebar> = {
  APPRENANT: {
    accent: 'from-codialis-sky to-codialis-blue',
    initiales: 'TF',
    titre: LIBELLE_MARQUE,
    sousTitre: 'Mon parcours',
    sections: [
      {
        titre: 'Mon parcours',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'mycourses', libelle: 'Mes formations', icone: 'presse' },
          { ecran: 'catalog', libelle: 'Catalogue', icone: 'livre' },
          { ecran: 'app-documents', libelle: 'Mes documents', icone: 'document' },
          { ecran: 'app-timeline', libelle: 'Ma timeline', icone: 'parcours' },
          { ecran: 'calendar', libelle: 'Calendrier', icone: 'calendrier' },
        ],
      },
      {
        titre: 'Apprentissage',
        entrees: [
          { ecran: 'player', libelle: 'Lecteur de cours', icone: 'lecture' },
          { ecran: 'assignments', libelle: 'Devoirs à rendre', icone: 'document' },
          { ecran: 'quiz', libelle: 'Quiz interactif', icone: 'question' },
          { ecran: 'ai-tutor', libelle: 'Tuteur IA', icone: 'ia', etiquette: 'IA' },
          { ecran: 'certificates', libelle: 'Mes certificats', icone: 'medaille' },
        ],
      },
      {
        titre: 'Suivi',
        entrees: [
          { ecran: 'emargement', libelle: 'Mon émargement', icone: 'stylo' },
          { ecran: 'app-questionnaires', libelle: 'Questionnaires', icone: 'etoile' },
          // Dépôt et suivi de ses réclamations (TF-482).
          { ecran: 'complaints', libelle: 'Mes réclamations', icone: 'reclamation' },
        ],
      },
      {
        titre: 'Espace social',
        entrees: [
          {
            ecran: 'messages',
            libelle: 'Messagerie',
            icone: 'message',
            pastille: { valeur: '5', ton: 'info' },
          },
        ],
      },
      COMPTE,
    ],
  },

  FORMATEUR: {
    accent: 'from-amber-500 to-orange-600',
    initiales: 'EF',
    titre: 'Espace Formateur',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'instructor-promos', libelle: 'Mes promotions', icone: 'utilisateurs' },
          { ecran: 'calendar', libelle: 'Mon planning', icone: 'calendrier' },
        ],
      },
      {
        titre: 'Mes contenus',
        entrees: [
          { ecran: 'instructor-courses', libelle: 'Mes cours', icone: 'livre' },
          { ecran: 'instructor-create', libelle: 'Créer un cours', icone: 'stylo' },
          {
            ecran: 'instructor-ai-gen',
            libelle: 'Génération IA',
            icone: 'ia',
            etiquette: 'IA',
          },
          { ecran: 'form-elearning', libelle: 'Salle e-learning', icone: 'lecture' },
        ],
      },
      {
        titre: 'Évaluations',
        entrees: [
          { ecran: 'instructor-grading', libelle: 'Corrections', icone: 'document' },
          { ecran: 'quiz', libelle: 'Quiz et exercices', icone: 'question' },
          { ecran: 'emargement', libelle: 'Émargement session', icone: 'stylo' },
        ],
      },
      {
        titre: 'Suivi pédagogique',
        entrees: [
          { ecran: 'learners', libelle: 'Mes apprenants', icone: 'utilisateurs' },
          {
            ecran: 'form-fiches',
            libelle: 'Fiches séquence',
            icone: 'document',
          },
          { ecran: 'form-heures', libelle: 'Mes heures', icone: 'horloge' },
          { ecran: 'form-fin', libelle: 'Fin de formation & ECF', icone: 'medaille' },
          { ecran: 'instructor-stats', libelle: 'Statistiques', icone: 'graphique' },
        ],
      },
      {
        titre: 'Facturation',
        entrees: [
          { ecran: 'form-facturation', libelle: 'Mes factures', icone: 'euro' },
        ],
      },
      {
        titre: 'Communication',
        entrees: [
          { ecran: 'messages', libelle: 'Messagerie', icone: 'message' },
          // Dépôt et suivi de ses réclamations (TF-482).
          { ecran: 'complaints', libelle: 'Mes réclamations', icone: 'reclamation' },
        ],
      },
      COMPTE,
    ],
  },

  ADMINISTRATIF: {
    accent: 'from-codialis-sky to-codialis-blue',
    initiales: 'AD',
    titre: 'Pôle administratif',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'parcours', libelle: 'Parcours apprenant', icone: 'parcours' },
          { ecran: 'sites', libelle: 'Multi-sites', icone: 'globe' },
        ],
      },
      {
        // Les trois entrées de la maquette. « Dossier apprenant » ne peut pas
        // désigner un apprenant depuis une rubrique de navigation : elle mène à
        // l'écran de sélection, qui renvoie vers les deux listes.
        titre: 'Dossiers',
        entrees: [
          {
            ecran: 'adm-inscriptions',
            libelle: 'Inscriptions & entretiens',
            icone: 'presse',
            pastille: { valeur: '5', ton: 'warning' },
          },
          { ecran: 'adm-dossier', libelle: 'Dossier apprenant', icone: 'dossier' },
          { ecran: 'adm-contrats-apprentissage', libelle: 'Contrats d\'apprentissage', icone: 'document' },
          { ecran: 'learners', libelle: 'Tous les apprenants', icone: 'utilisateurs' },
          // Référentiel entreprise commun : l'administratif l'ouvre en montant
          // un contrat, et crée l'entreprise qui y manque depuis le recueil.
          { ecran: 'ped-repertoire', libelle: 'Entreprises & partenaires', icone: 'livre' },
          { ecran: 'com-entreprises', libelle: 'Recueil entreprise & besoins', icone: 'batiment' },
          { ecran: 'periode-immersion', libelle: "Périodes d'immersion", icone: 'horloge' },
        ],
      },
      {
        titre: 'Documents',
        entrees: [
          { ecran: 'adm-formations', libelle: 'Formations & programmes', icone: 'chapeauDiplome' },
          {
            ecran: 'adm-ruptures',
            libelle: 'Ruptures de contrat',
            icone: 'triangleAlerte',
          },
          { ecran: 'adm-export', libelle: 'Export & suivi partagé', icone: 'graphique' },
          { ecran: 'bpf', libelle: 'Bilan pédagogique BPF', icone: 'tendance' },
        ],
      },
      {
        titre: 'Gestion documentaire',
        entrees: [
          { ecran: 'adm-pieces', libelle: 'Pièces justificatives', icone: 'trombone' },
          { ecran: 'adm-antennes', libelle: 'Antennes', icone: 'batiment' },
          // Vérification des pièces et factures transmises par les formateurs.
          { ecran: 'instructors', libelle: 'Formateurs', icone: 'utilisateurs' },
        ],
      },
      {
        titre: 'Suivi',
        entrees: [
          { ecran: 'alertes', libelle: 'Alertes', icone: 'cloche' },
          { ecran: 'com-relances', libelle: 'Relances mail / SMS', icone: 'actualiser' },
          // Registre des réclamations de ses antennes (TF-482).
          { ecran: 'complaints', libelle: 'Réclamations', icone: 'reclamation' },
          // L'assiduité conditionne le contrat : l'administratif doit pouvoir
          // vérifier qu'un candidat vient, sans passer par le pôle pédagogique.
          { ecran: 'emargement', libelle: 'Émargement & présences', icone: 'stylo' },
          { ecran: 'calendar', libelle: 'Calendrier', icone: 'calendrier' },
          {
            ecran: 'messages',
            libelle: 'Messagerie',
            icone: 'message',
            pastille: { valeur: '5', ton: 'info' },
          },
        ],
      },
      COMPTE,
    ],
  },

  RECRUTEMENT: {
    accent: 'from-codialis-mint to-codialis-teal',
    initiales: 'RC',
    titre: 'Recrutement candidats',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'parcours', libelle: 'Parcours apprenant', icone: 'parcours' },
          {
            ecran: 'com-entretiens',
            libelle: 'Suivi des entretiens',
            icone: 'utilisateurs',
            pastille: { valeur: '11', ton: 'info' },
          },
          { ecran: 'calendar', libelle: 'Calendrier', icone: 'calendrier' },
        ],
      },
      {
        titre: 'Candidats',
        entrees: [
          // Le recrutement crée le dossier du candidat qu'il reçoit.
          { ecran: 'adm-inscriptions', libelle: 'Inscriptions & entretiens', icone: 'presse' },
          { ecran: 'learners', libelle: 'Candidats & apprenants', icone: 'utilisateurs' },
          { ecran: 'adm-dossier', libelle: 'Dossier candidat', icone: 'dossier' },
          { ecran: 'periode-immersion', libelle: "Périodes d'immersion", icone: 'horloge' },
          {
            ecran: 'ped-sanscontrat',
            libelle: 'Sans entreprise',
            icone: 'oeil',
          },
        ],
      },
      {
        // CDC §5.1 : le recrutement tient la fiche de recueil entreprise et le
        // recueil des besoins, et positionne ses candidats par le matching. Il
        // a aussi tous les droits de la relation entreprise, réglage du barème
        // compris (décisions du 12/10/2026).
        titre: 'Entreprises',
        entrees: [
          {
            ecran: 'com-entreprises',
            libelle: 'Recueil entreprise & besoins',
            icone: 'batiment',
          },
          { ecran: 'ped-repertoire', libelle: 'Répertoire partenaires', icone: 'dossier' },
          { ecran: 'com-matching', libelle: 'Matching entreprise', icone: 'etoile' },
          { ecran: 'com-matching-criteres', libelle: 'Critères de matching', icone: 'reglages' },
        ],
      },
      {
        titre: 'Relation',
        entrees: [
          { ecran: 'com-relances', libelle: 'Relances mail / SMS', icone: 'message' },
          { ecran: 'alertes', libelle: 'Alertes', icone: 'cloche' },
          {
            ecran: 'messages',
            libelle: 'Messagerie',
            icone: 'message',
            pastille: { valeur: '5', ton: 'info' },
          },
        ],
      },
      COMPTE,
    ],
  },

  RELATION_ENTREPRISE: {
    accent: 'from-codialis-mint to-codialis-teal',
    initiales: 'RE',
    titre: 'Relation entreprises',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'parcours', libelle: 'Parcours apprenant', icone: 'parcours' },
          { ecran: 'calendar', libelle: 'Calendrier', icone: 'calendrier' },
        ],
      },
      {
        titre: 'Entreprises',
        entrees: [
          { ecran: 'com-entreprises', libelle: 'Entreprises & prospection', icone: 'batiment' },
          { ecran: 'ped-repertoire', libelle: 'Répertoire partenaires', icone: 'dossier' },
          { ecran: 'com-matching', libelle: 'Matching entreprise', icone: 'etoile' },
          { ecran: 'periode-immersion', libelle: "Périodes d'immersion", icone: 'horloge' },
          { ecran: 'com-matching-criteres', libelle: 'Critères de matching', icone: 'reglages' },
        ],
      },
      {
        titre: 'Positionnement',
        entrees: [
          { ecran: 'learners', libelle: 'Candidats & apprenants', icone: 'utilisateurs' },
          { ecran: 'adm-dossier', libelle: 'Dossier candidat', icone: 'dossier' },
          {
            ecran: 'ped-sanscontrat',
            libelle: 'Sans entreprise',
            icone: 'oeil',
          },
        ],
      },
      {
        titre: 'Relation',
        entrees: [
          { ecran: 'com-relances', libelle: 'Relances mail / SMS', icone: 'message' },
          { ecran: 'alertes', libelle: 'Alertes', icone: 'cloche' },
          {
            ecran: 'messages',
            libelle: 'Messagerie',
            icone: 'message',
            pastille: { valeur: '5', ton: 'info' },
          },
        ],
      },
      COMPTE,
    ],
  },

  PEDAGOGIQUE: {
    accent: 'from-codialis-mint to-codialis-teal',
    initiales: 'PE',
    titre: 'Pôle pédagogique',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'ped-hub', libelle: 'Vue d’ensemble du pôle', icone: 'parcours' },
          {
            ecran: 'ped-integration',
            libelle: 'Intégration & convocations',
            icone: 'utilisateurs',
            pastille: { valeur: '3', ton: 'warning' },
          },
          { ecran: 'sites', libelle: 'Multi-sites', icone: 'batiment' },
        ],
      },
      {
        titre: 'Suivi de formation',
        entrees: [
          { ecran: 'emargement', libelle: 'Émargement', icone: 'stylo' },
          {
            ecran: 'ped-absences',
            libelle: 'Absences & avertissements',
            icone: 'cloche',
            pastille: { valeur: '4', ton: 'danger' },
          },
          { ecran: 'form-elearning', libelle: 'Salle e-learning', icone: 'lecture' },
          {
            ecran: 'form-fiches',
            libelle: 'Fiches séquence',
            icone: 'document',
          },
        ],
      },
      {
        titre: 'Évaluation',
        entrees: [
          { ecran: 'ped-tests', libelle: 'Tests de positionnement', icone: 'question' },
          { ecran: 'form-fin', libelle: 'Fin de formation & ECF', icone: 'medaille' },
        ],
      },
      {
        titre: 'Accompagnement',
        entrees: [
          { ecran: 'learners', libelle: 'Apprenants', icone: 'utilisateurs' },
          { ecran: 'ped-documents', libelle: 'Documents pédagogiques', icone: 'document' },
          { ecran: 'instructors', libelle: 'Formateurs', icone: 'utilisateurs' },
          { ecran: 'ped-accompagnement', libelle: 'Accompagnement', icone: 'utilisateurs' },
          {
            ecran: 'ped-sanscontrat',
            libelle: 'Sans entreprise',
            icone: 'oeil',
          },
          // L'assistant(e) seul(e), à l'essai (TF-468) : l'API n'envoie l'écran
          // ni au responsable ni en production.
          { ecran: 'ped-repertoire', libelle: 'Répertoire partenaires', icone: 'dossier' },
        ],
      },
      {
        titre: 'Ingénierie & sessions',
        entrees: [
          { ecran: 'ped-ingenierie', libelle: 'Ingénierie pédagogique', icone: 'livre' },
          { ecran: 'ped-sessions', libelle: 'Sessions', icone: 'calendrier' },
          { ecran: 'ped-reporting', libelle: 'Reporting', icone: 'graphique' },
          { ecran: 'bpf', libelle: 'Bilan pédagogique BPF', icone: 'tendance' },
        ],
      },
      {
        titre: 'Veille & alertes',
        entrees: [
          { ecran: 'ped-veille', libelle: 'Veille réglementaire', icone: 'bouclier' },
          { ecran: 'qualiopi', libelle: 'Conformité Qualiopi', icone: 'bouclier' },
          // Registre des réclamations, preuve de l'indicateur 31 (TF-482).
          { ecran: 'complaints', libelle: 'Réclamations', icone: 'reclamation' },
          { ecran: 'alertes', libelle: 'Gestion des alertes', icone: 'cloche' },
          { ecran: 'satisfaction', libelle: 'Satisfaction', icone: 'etoile' },
        ],
      },
      {
        titre: 'Communication',
        entrees: [
          {
            ecran: 'messages',
            libelle: 'Messagerie',
            icone: 'message',
            pastille: { valeur: '5', ton: 'info' },
          },
        ],
      },
      COMPTE,
    ],
  },

  FACTURATION: {
    accent: 'from-codialis-navy to-codialis-blue',
    initiales: 'FA',
    titre: 'Pôle facturation',
    sousTitre: LIBELLE_MARQUE,
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'fac-formateurs', libelle: 'Factures formateurs', icone: 'euro' },
          { ecran: 'fac-circuit', libelle: 'Circuit & transmission', icone: 'parcours' },
        ],
      },
      {
        titre: 'Financement',
        entrees: [
          { ecran: 'financing', libelle: 'Financeurs & OPCO', icone: 'euro' },
          // Liste et dossier complet de l'administratif, en lecture (TF-464).
          { ecran: 'learners', libelle: 'Dossiers apprenants', icone: 'dossier' },
          { ecran: 'bpf', libelle: 'Bilan pédagogique BPF', icone: 'tendance' },
          { ecran: 'invoicing', libelle: 'Factures', icone: 'document' },
          { ecran: 'fac-parametres', libelle: 'Paramètres de facturation', icone: 'reglages' },
        ],
      },
      {
        // Le pôle lit pièces, factures et heures des formateurs dans
        // « Formateurs » ; l'« Espace facturation formateur » est celui du
        // formateur lui-même, qu'il n'a plus (TF-424).
        titre: 'Formateurs',
        entrees: [{ ecran: 'instructors', libelle: 'Formateurs', icone: 'utilisateurs' }],
      },
      {
        titre: 'Suivi',
        entrees: [
          { ecran: 'alertes', libelle: 'Alertes', icone: 'cloche' },
          { ecran: 'messages', libelle: 'Messagerie', icone: 'message' },
        ],
      },
      COMPTE,
    ],
  },

  DIRECTION: {
    accent: 'from-codialis-mint to-codialis-teal',
    initiales: 'TF',
    titre: LIBELLE_MARQUE,
    sousTitre: 'Direction',
    sections: [
      {
        titre: 'Pilotage',
        entrees: [
          { ecran: 'dashboard', libelle: 'Tableau de bord', icone: 'accueil' },
          { ecran: 'dir-syntheses', libelle: 'Synthèses mensuelles', icone: 'graphique' },
          { ecran: 'dir-vue-globale', libelle: 'Vue globale', icone: 'globe' },
          // Heures, complétion, satisfaction et impact économique (TF-479) : du pilotage, pas du financement.
          { ecran: 'analytics', libelle: 'Analytics & ROI', icone: 'tendance' },
          // Période, critères, filtres et comparaison au choix, exports Excel et PDF (TF-480).
          { ecran: 'dir-rapports', libelle: 'Rapports', icone: 'liste' },
          // La vue par financeur du pôle facturation, en consultation (TF-489).
          { ecran: 'dir-financeurs', libelle: 'Pilotage des financeurs', icone: 'euro' },
          { ecran: 'dir-apprenants-risque', libelle: 'Apprenants à risque', icone: 'triangleAlerte' },
          // Entreprises où sont les apprenants, par secteur, pour préparer les visites (TF-481).
          { ecran: 'dir-apprenants-entreprises', libelle: 'Apprenants & entreprises', icone: 'batiment' },
          { ecran: 'dir-preinscriptions', libelle: 'Préinscriptions', icone: 'presse' },
          { ecran: 'dir-conventions', libelle: 'Conventions', icone: 'stylo' },
          { ecran: 'dir-modifications', libelle: 'Modifications sensibles', icone: 'bouclierCoche' },
          { ecran: 'dir-parametres', libelle: 'Paramétrage', icone: 'reglages' },
          { ecran: 'dir-journal', libelle: 'Journal d’audit', icone: 'journal' },
          { ecran: 'catalog', libelle: 'Catalogue', icone: 'livre' },
          { ecran: 'parcours', libelle: 'Parcours apprenant', icone: 'parcours' },
          { ecran: 'sites', libelle: 'Multi-sites', icone: 'batiment' },
          // Comptes du personnel et des formateurs, invitations (TF-500).
          { ecran: 'dir-utilisateurs', libelle: 'Utilisateurs', icone: 'utilisateurs' },
          { ecran: 'dir-attributions', libelle: 'Attributions', icone: 'utilisateurs' },
          { ecran: 'calendar', libelle: 'Calendrier', icone: 'calendrier' },
        ],
      },
      {
        titre: 'Pôles',
        entrees: [
          { ecran: 'adm-inscriptions', libelle: 'Administratif', icone: 'dossier' },
          { ecran: 'com-entreprises', libelle: 'Commercial', icone: 'batiment' },
          { ecran: 'ped-hub', libelle: 'Pédagogique', icone: 'livre' },
          { ecran: 'fac-circuit', libelle: 'Facturation', icone: 'euro' },
        ],
      },
      {
        titre: 'Gestion',
        entrees: [
          { ecran: 'learners', libelle: 'Apprenants', icone: 'utilisateurs' },
          { ecran: 'instructors', libelle: 'Formateurs', icone: 'utilisateurs' },
          { ecran: 'adm-formations', libelle: 'Formations & programmes', icone: 'chapeauDiplome' },
          { ecran: 'adm-antennes', libelle: 'Antennes & En-têtes', icone: 'batiment' },
          { ecran: 'emargement', libelle: 'Émargement digital', icone: 'stylo' },
          // Toute la file de corrections, avec son correcteur (TF-457).
          { ecran: 'instructor-grading', libelle: 'Corrections & évaluations', icone: 'document' },
          // Toutes les promotions en formation, leur avancement et leurs retards (TF-458).
          { ecran: 'instructor-promos', libelle: 'Suivi des promotions', icone: 'presse' },
          // Heures, vues des leçons et retours d'un formateur de ses antennes (TF-459).
          { ecran: 'instructor-stats', libelle: 'Statistiques formateurs', icone: 'graphique' },
          { ecran: 'qualiopi', libelle: 'Conformité Qualiopi', icone: 'bouclier' },
          // Registre des réclamations de toutes les antennes (TF-482).
          { ecran: 'complaints', libelle: 'Réclamations', icone: 'reclamation' },
        ],
      },
      {
        titre: 'Financement',
        entrees: [
          { ecran: 'financing', libelle: 'Financeurs & OPCO', icone: 'euro' },
          { ecran: 'bpf', libelle: 'Bilan pédagogique BPF', icone: 'tendance' },
          { ecran: 'invoicing', libelle: 'Factures', icone: 'document' },
          { ecran: 'fac-parametres', libelle: 'Paramètres facturation', icone: 'reglages' },
        ],
      },
      {
        titre: 'Qualité & IA',
        entrees: [
          { ecran: 'satisfaction', libelle: 'Satisfaction', icone: 'etoile' },
          { ecran: 'ai-monitoring', libelle: 'Supervision IA', icone: 'oeil' },
        ],
      },
      {
        titre: 'Communication',
        entrees: [
          { ecran: 'messages', libelle: 'Messagerie', icone: 'message' },
        ],
      },
      COMPTE,
    ],
  },
};

/** Ajustements d'en-tête propres aux sous-profils (CDC §6.1). */
/**
 * Un formateur salarié est payé par la paie (CDC §7.4) : son espace ne lui
 * demande que ses pièces de qualification, sans facture. La rubrique se nomme
 * donc d'après ce qu'il y trouve.
 */
export function sectionsDuFormateur(
  sections: SectionNavigation[],
  statut: 'SALARIE' | 'INDEPENDANT' | null,
): SectionNavigation[] {
  if (statut !== 'SALARIE') return sections;
  return sections.map((section) =>
    section.entrees.some((entree) => entree.ecran === 'form-facturation')
      ? {
          titre: 'Documents',
          entrees: section.entrees.map((entree) =>
            entree.ecran === 'form-facturation'
              ? { ...entree, libelle: 'Mes documents', icone: 'document' }
              : entree,
          ),
        }
      : section,
  );
}

export const ENTETES_SOUS_PROFIL: Record<SousProfil, { titre: string; initiales: string }> = {
  ASSISTANT: { titre: 'Assistance pédagogique', initiales: 'AP' },
};
